/** Form building blocks shared by the series and one-off event editors. */
import type { ReactNode } from 'react';
import { AUDIENCE_GROUPS, type AudienceGroup } from '../../../shared/audience';
import type { EventKind } from '../../../shared/dto/events';
import { useCollections } from '../../api/calendar';
import { SECTION_COLOR } from '../../components/people';
import { ChipGroup, Select, TextInput } from '../../components/ui';
import { useI18n, useT, type MessageKey, type T } from '../../i18n';
import { durationBetween, minutesOf } from '../../lib/dates';
import { audienceLabelKey, EVENT_KINDS, kindKey } from './shared';

export const TITLE_MAX = 120;
export const LOCATION_MAX = 200;
export const DESCRIPTION_MAX = 2000;

export function FieldError({ children }: { children: ReactNode }) {
  return <p className="text-[15px] text-[var(--danger-text)]">{children}</p>;
}

export function KindChips({ value, onChange }: { value: EventKind; onChange: (k: EventKind) => void }) {
  const t = useT();
  return (
    <ChipGroup<EventKind>
      single
      label={t('calendar.form.kind')}
      options={EVENT_KINDS.map((k) => ({ value: k, label: t(kindKey(k)) }))}
      value={[value]}
      onChange={(v) => v[0] && onChange(v[0])}
    />
  );
}

/** Audience picker: "Allur kórinn" is exclusive with the individual groups. */
export function AudienceChips({ value, onChange, error }: { value: AudienceGroup[]; onChange: (v: AudienceGroup[]) => void; error?: string | null }) {
  const t = useT();
  const handle = (next: AudienceGroup[]) => {
    const added = next.filter((g) => !value.includes(g));
    if (added.includes('all')) onChange(['all']);
    else if (added.length > 0) onChange(next.filter((g) => g !== 'all'));
    else onChange(next);
  };
  return (
    <div className="flex flex-col gap-1.5">
      <ChipGroup<AudienceGroup>
        label={t('calendar.form.audience')}
        options={AUDIENCE_GROUPS.map((g) => ({ value: g, label: t(audienceLabelKey(g)), color: g === 'all' ? undefined : SECTION_COLOR[g] }))}
        value={value}
        onChange={handle}
      />
      {error ? <FieldError>{error}</FieldError> : null}
    </div>
  );
}

const WEEKDAY_VALUES = ['1', '2', '3', '4', '5', '6', '7'] as const;
type WeekdayValue = (typeof WEEKDAY_VALUES)[number];

export function WeekdayChips({ value, onChange, error }: { value: number[]; onChange: (v: number[]) => void; error?: string | null }) {
  const t = useT();
  return (
    <div className="flex flex-col gap-1.5">
      <ChipGroup<WeekdayValue>
        label={t('calendar.form.weekdays')}
        options={WEEKDAY_VALUES.map((d) => ({ value: d, label: t(`calendar.wd.${d}`) }))}
        value={value.map((d) => String(d) as WeekdayValue)}
        onChange={(v) => onChange(v.map(Number).sort((a, b) => a - b))}
      />
      {error ? <FieldError>{error}</FieldError> : null}
    </div>
  );
}

export function IntervalChips({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  const t = useT();
  const options: { value: string; label: string }[] = [
    { value: '1', label: t('calendar.form.weekly') },
    { value: '2', label: t('calendar.form.biweekly') },
  ];
  if (value > 2) options.push({ value: String(value), label: t('calendar.form.every_n_weeks', { count: value }) });
  return <ChipGroup single label={t('calendar.form.interval')} options={options} value={[String(value)]} onChange={(v) => v[0] && onChange(Number(v[0]))} />;
}

/** Start/end time inputs (24 h) with a duration hint; an end at or before the start means the next day. */
export function TimeRangeFields({
  start,
  end,
  onStart,
  onEnd,
  error,
}: {
  start: string;
  end: string;
  onStart: (v: string) => void;
  onEnd: (v: string) => void;
  error?: string | null;
}) {
  const t = useT();
  let hint: string | null = null;
  let nextDay = false;
  if (/^\d{2}:\d{2}$/.test(start) && /^\d{2}:\d{2}$/.test(end)) {
    const d = durationBetween(start, end);
    nextDay = minutesOf(end) <= minutesOf(start);
    const h = Math.floor(d / 60);
    const m = d % 60;
    hint = h > 0 && m > 0 ? t('calendar.form.duration_hm', { h, m }) : h > 0 ? t('calendar.form.duration_h', { h }) : t('calendar.form.duration_m', { m });
  }
  return (
    <div className="flex flex-col gap-1.5">
      <div className="grid grid-cols-2 gap-3">
        <TextInput type="time" label={t('calendar.form.start_time')} value={start} onChange={(e) => onStart(e.target.value)} step={300} required />
        <TextInput type="time" label={t('calendar.form.end_time')} value={end} onChange={(e) => onEnd(e.target.value)} step={300} required />
      </div>
      {error ? (
        <FieldError>{error}</FieldError>
      ) : hint ? (
        <p className="text-[14px] text-[var(--muted)]">
          {hint}
          {nextDay ? ` · ${t('calendar.form.next_day')}` : ''}
        </p>
      ) : null}
    </div>
  );
}

const CHECKIN_OPTIONS = [0, 15, 30, 45, 60, 90, 120, 180, 240];

export function CheckinOpensSelect({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  const t = useT();
  const opts = CHECKIN_OPTIONS.includes(value) ? CHECKIN_OPTIONS : [...CHECKIN_OPTIONS, value].sort((a, b) => a - b);
  const label = (n: number) =>
    n === 0
      ? t('calendar.form.checkin_at_start')
      : n >= 60 && n % 60 === 0
        ? t('calendar.form.checkin_hours', { count: n / 60 })
        : t('calendar.form.checkin_min', { count: n });
  return (
    <Select label={t('calendar.form.checkin_opens')} value={String(value)} onChange={(e) => onChange(Number(e.target.value))}>
      {opts.map((n) => (
        <option key={n} value={n}>
          {label(n)}
        </option>
      ))}
    </Select>
  );
}

/** Setlist picker (collections of kind "setlist"). Value '' = no setlist. */
export function SetlistSelect({ value, onChange }: { value: string; onChange: (id: string) => void }) {
  const t = useT();
  const { lang } = useI18n();
  const q = useCollections();
  const collator = new Intl.Collator('is');
  const setlists = (q.data ?? []).filter((c) => c.kind === 'setlist').sort((a, b) => a.sort - b.sort || collator.compare(a.name, b.name));
  const missing = value && !setlists.some((c) => c.id === value);
  return (
    <Select label={t('calendar.form.setlist')} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{t('calendar.form.no_setlist')}</option>
      {setlists.map((c) => (
        <option key={c.id} value={c.id}>
          {c.name}
        </option>
      ))}
      {missing ? <option value={value}>{q.isLoading ? t('common.loading') : (q.data?.find((c) => c.id === value)?.name ?? '…')}</option> : null}
    </Select>
  );
}

/** "Vikulega á miðvikudögum kl. 19:30–22:00" */
export function seriesSummary(t: T, loc: string, weekdays: number[], interval: number, start: string, end: string): string | null {
  if (weekdays.length === 0) return null;
  const days = weekdays.map((d) => t(`calendar.wd_on.${d}` as MessageKey));
  const list = new Intl.ListFormat(loc, { style: 'long', type: 'conjunction' }).format(days);
  const time = `${start}–${end}`;
  if (interval === 1) return t('calendar.form.summary_weekly', { days: list, time });
  if (interval === 2) return t('calendar.form.summary_biweekly', { days: list, time });
  return `${t('calendar.form.every_n_weeks', { count: interval })}: ${list} ${time}`;
}
